/**
 * adminApi.js — shared auth/API helpers for the redesigned admin portal.
 *
 * Conventions are replicated from the legacy pages
 * (frontend/src/pages/Admin*.js, ManagerLoginPage.js):
 *   - JWT stored in localStorage.adminToken
 *   - expiry timestamp (ms) in localStorage.adminTokenExpires
 *     (set on login as Date.now() + expires_in * 1000)
 *   - managerMode === "true" for order managers (login via /manager)
 *   - all calls: fetch(`${process.env.REACT_APP_BACKEND_URL}/api/...`)
 *     with `Authorization: Bearer <token>`
 *   - missing/expired token -> redirect to /admin/login
 *     (or /manager when managerMode === "true")
 */

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** True when the operator signed in through /manager (orders-only portal). */
export function isManagerMode() {
  return localStorage.getItem("managerMode") === "true";
}

/** The login route for the current portal identity. */
export function adminLoginPath() {
  return isManagerMode() ? "/manager" : "/admin/login";
}

/**
 * Returns the stored admin JWT, or null when missing/expired.
 * If adminTokenExpires exists and is in the past, the token is cleared
 * (same cleanup the legacy logout handlers perform) and null is returned.
 */
export function getAdminToken() {
  const token = localStorage.getItem("adminToken");
  if (!token) return null;
  const expires = Number(localStorage.getItem("adminTokenExpires"));
  if (expires && Date.now() > expires) {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpires");
    return null;
  }
  return token;
}

/**
 * Legacy-style header builder (mirrors getAuthHeaders() in the old pages).
 * Returns null when there is no valid token — callers should bail out
 * (adminFetch below handles the redirect for you; prefer adminFetch).
 */
export function getAuthHeaders(extra = {}) {
  const token = getAdminToken();
  if (!token) return null;
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...extra,
  };
}

/** Error thrown when a request could not be made/authorised. */
export class AdminAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = "AdminAuthError";
    this.isAuthError = true;
  }
}

function redirectTo(path, navigate) {
  if (typeof navigate === "function") {
    navigate(path);
  } else if (typeof window !== "undefined") {
    window.location.href = path;
  }
}

/**
 * Authenticated fetch against the backend.
 *
 *   const res = await adminFetch(`/api/admin/orders?${params}`);
 *   if (!res.ok) throw new Error("Failed to fetch orders");
 *   const data = await res.json();
 *
 * - Prefixes process.env.REACT_APP_BACKEND_URL.
 * - Adds `Authorization: Bearer <token>` and `Content-Type: application/json`
 *   (Content-Type is omitted when body is FormData so the browser can set the
 *   multipart boundary — e.g. POST /api/admin/upload/image).
 * - Missing/expired token OR a 401 response: clears the token and redirects to
 *   /admin/login (or /manager in manager mode), then throws AdminAuthError.
 *   Catch blocks can check `err.isAuthError` to skip showing an error banner.
 * - Returns the raw Response (never auto-parses) so each screen can keep the
 *   exact response/error handling of its legacy page (.json(), .blob(), .ok,
 *   status-specific branches like the 403 on database export, etc).
 *
 * options: standard fetch options + optional `navigate` (react-router's
 * navigate fn) for SPA redirects; falls back to window.location.
 */
export async function adminFetch(path, options = {}) {
  const { navigate, headers: extraHeaders, ...fetchOptions } = options;

  // Compute the redirect target BEFORE any storage mutation.
  const loginPath = adminLoginPath();

  const token = getAdminToken();
  if (!token) {
    redirectTo(loginPath, navigate);
    throw new AdminAuthError("Not authenticated");
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    ...(fetchOptions.body instanceof FormData
      ? {}
      : { "Content-Type": "application/json" }),
    ...(extraHeaders || {}),
  };

  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...fetchOptions,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpires");
    redirectTo(loginPath, navigate);
    throw new AdminAuthError("Session expired");
  }

  return response;
}

/**
 * Logs the operator out — clears adminToken, adminTokenExpires and managerMode
 * (exactly like the legacy handleLogout / handleManagerLogout) and redirects
 * to the matching login screen (/manager for managers, /admin/login otherwise).
 * Pass react-router's navigate for an SPA redirect; omits => full page load.
 */
export function adminLogout(navigate) {
  const target = adminLoginPath(); // decide BEFORE clearing managerMode
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminTokenExpires");
  localStorage.removeItem("managerMode");
  redirectTo(target, navigate);
}
